/**
 * @fileoverview Event Bus System for Decoupled Component Communication
 * 
 * This file implements a robust event bus system for managing application-wide
 * communication between components without tight coupling. The event bus pattern
 * allows components to communicate through events without direct references,
 * improving modularity and maintainability.
 * 
 * Key Features:
 * - Type-safe event handling with TypeScript generics
 * - Asynchronous and synchronous event emission
 * - One-time event listeners with automatic cleanup
 * - Memory leak prevention through proper handler management
 * - Wildcard event support for debugging and logging
 * - Error isolation to prevent one handler from breaking others
 * 
 * Architecture:
 * - IEventBus: Core interface defining the contract
 * - EventBus: Standard implementation with Map-based storage
 * - TypedEventBus: Type-safe wrapper for strongly-typed events
 * - Global singleton for application-wide event coordination
 * 
 * Use Cases:
 * - Tool activation/deactivation notifications
 * - Canvas state changes (zoom, pan, selection)
 * - Command execution results and undo/redo events
 * - Model updates and data synchronization
 * - UI state transitions and modal management
 * 
 * @author Claude Code
 * @version 1.0.0
 */

/**
 * Type definition for event handler functions.
 * 
 * Handlers can be either synchronous or asynchronous and accept
 * variable arguments based on the specific event being handled.
 * 
 * @example
 * ```typescript
 * // Synchronous handler
 * const syncHandler: EventHandler = (data) => {
 *   console.log('Event received:', data);
 * };
 * 
 * // Asynchronous handler
 * const asyncHandler: EventHandler = async (data) => {
 *   await saveToDatabase(data);
 * };
 * ```
 */
export type EventHandler = (...args: any[]) => void | Promise<void>;

/**
 * Function type for unsubscribing from events.
 * 
 * Returned by event subscription methods to allow easy cleanup.
 * Calling this function removes the associated event handler.
 * 
 * @example
 * ```typescript
 * const unsubscribe = eventBus.on('user-action', handler);
 * // Later, when cleanup is needed:
 * unsubscribe();
 * ```
 */
export type EventUnsubscribe = () => void;

/**
 * Core event bus interface defining the contract for event management.
 * 
 * This interface provides the foundation for all event bus implementations,
 * ensuring consistent behavior across different event systems. It supports
 * both synchronous and asynchronous event handling with proper cleanup.
 * 
 * @example
 * ```typescript
 * const eventBus: IEventBus = new EventBus();
 * 
 * // Subscribe to events
 * const unsubscribe = eventBus.on('data-updated', (data) => {
 *   updateUI(data);
 * });
 * 
 * // Emit events
 * eventBus.emit('data-updated', { id: 1, value: 'new data' });
 * 
 * // Clean up when done
 * unsubscribe();
 * ```
 */
export interface IEventBus {
  /**
   * Subscribe to an event with a persistent handler.
   * 
   * The handler will be called every time the event is emitted until
   * manually unsubscribed. Returns an unsubscribe function for cleanup.
   * 
   * @param event - The event name to listen for
   * @param handler - Function to call when event is emitted
   * @returns Function to call for unsubscribing from the event
   * 
   * @example
   * ```typescript
   * const unsubscribe = eventBus.on('node-selected', (nodeId) => {
   *   highlightNode(nodeId);
   * });
   * ```
   */
  on(event: string, handler: EventHandler): EventUnsubscribe;
  
  /**
   * Unsubscribe a specific handler from an event.
   * 
   * Removes the exact handler function from the event's listener list.
   * If the handler is not found, the operation is silently ignored.
   * 
   * @param event - The event name to unsubscribe from
   * @param handler - The exact handler function to remove
   * 
   * @example
   * ```typescript
   * eventBus.off('node-selected', myHandler);
   * ```
   */
  off(event: string, handler: EventHandler): void;
  
  /**
   * Subscribe to an event with a one-time handler.
   * 
   * The handler will be called only once when the event is emitted,
   * then automatically unsubscribed. Useful for initialization events
   * or one-time setup operations.
   * 
   * @param event - The event name to listen for
   * @param handler - Function to call once when event is emitted
   * @returns Function to call for manual unsubscribing if needed
   * 
   * @example
   * ```typescript
   * eventBus.once('app-initialized', () => {
   *   showWelcomeMessage();
   * });
   * ```
   */
  once(event: string, handler: EventHandler): EventUnsubscribe;
  
  /**
   * Emit an event synchronously to all registered handlers.
   * 
   * Calls all handlers for the event immediately in registration order.
   * Errors in individual handlers are caught and logged to prevent
   * one failing handler from affecting others.
   * 
   * @param event - The event name to emit
   * @param args - Arguments to pass to all event handlers
   * 
   * @example
   * ```typescript
   * // Emit with data
   * eventBus.emit('tool-activated', 'selection-tool', { mode: 'single' });
   * 
   * // Emit without data
   * eventBus.emit('canvas-cleared');
   * ```
   */
  emit(event: string, ...args: any[]): void;
  
  /**
   * Emit an event asynchronously to all registered handlers.
   * 
   * Calls all handlers concurrently and waits for all promises to resolve.
   * Useful when handlers need to perform async operations like API calls
   * or database updates before considering the event fully processed.
   * 
   * @param event - The event name to emit
   * @param args - Arguments to pass to all event handlers
   * @returns Promise that resolves when all handlers complete
   * 
   * @example
   * ```typescript
   * // Wait for all handlers to complete
   * await eventBus.emitAsync('data-saved', savedData);
   * console.log('All save operations completed');
   * ```
   */
  emitAsync(event: string, ...args: any[]): Promise<void>;
  
  /**
   * Remove all listeners for a specific event or all events.
   * 
   * If an event name is provided, removes all handlers for that event.
   * If no event name is provided, removes all handlers for all events.
   * Useful for cleanup during component unmounting or app shutdown.
   * 
   * @param event - Optional event name to clear (clears all if omitted)
   * 
   * @example
   * ```typescript
   * // Remove all handlers for a specific event
   * eventBus.removeAllListeners('node-updated');
   * 
   * // Remove all handlers for all events
   * eventBus.removeAllListeners();
   * ```
   */
  removeAllListeners(event?: string): void;
  
  /**
   * Get the number of active listeners for an event.
   * 
   * Returns the count of currently registered handlers for the specified
   * event. Useful for debugging, monitoring, or conditional logic based
   * on listener presence.
   * 
   * @param event - The event name to count listeners for
   * @returns Number of active listeners for the event
   * 
   * @example
   * ```typescript
   * const listenerCount = eventBus.getListenerCount('data-changed');
   * if (listenerCount === 0) {
   *   console.log('No components listening for data changes');
   * }
   * ```
   */
  getListenerCount(event: string): number;
}

/**
 * Concrete implementation of the IEventBus interface.
 * 
 * This class provides a full-featured event bus implementation using Map and Set
 * data structures for efficient event management. It includes error handling,
 * memory leak prevention, and wildcard event support for debugging.
 * 
 * Key Implementation Details:
 * - Uses Map<string, Set<EventHandler>> for O(1) event lookup
 * - WeakMap for tracking once handlers to enable proper cleanup
 * - Error isolation prevents failing handlers from affecting others
 * - Wildcard '*' events for debugging and global event monitoring
 * - Automatic cleanup of empty event sets to prevent memory leaks
 * 
 * @example
 * ```typescript
 * const eventBus = new EventBus();
 * 
 * // Subscribe to events
 * const unsubscribe = eventBus.on('user-login', (user) => {
 *   updateUserInterface(user);
 *   logUserActivity(user);
 * });
 * 
 * // Emit events
 * eventBus.emit('user-login', { id: 123, name: 'John' });
 * 
 * // One-time listeners
 * eventBus.once('app-startup', () => {
 *   initializeApplication();
 * });
 * 
 * // Async event handling
 * await eventBus.emitAsync('data-save', saveData);
 * 
 * // Cleanup
 * unsubscribe();
 * ```
 */
export class EventBus implements IEventBus {
  /** Map storing event names to their handler sets for efficient lookup */
  private events: Map<string, Set<EventHandler>> = new Map();
  
  /** WeakMap tracking once handlers to enable proper cleanup without memory leaks */
  private onceHandlers: WeakMap<EventHandler, EventHandler> = new WeakMap();
  
  /**
   * Subscribe to an event with a persistent handler.
   * 
   * Adds the handler to the event's listener set and returns an unsubscribe
   * function for cleanup. Creates the event set if it doesn't exist.
   * 
   * @param event - The event name to subscribe to
   * @param handler - The function to call when the event is emitted
   * @returns Unsubscribe function to remove this specific handler
   */
  on(event: string, handler: EventHandler): EventUnsubscribe {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    
    this.events.get(event)!.add(handler);
    
    // Return unsubscribe function
    return () => this.off(event, handler);
  }
  
  /**
   * Unsubscribe a specific handler from an event.
   * 
   * Removes the handler from the event's listener set. Handles both regular
   * handlers and once handlers (which use wrapper functions). Cleans up
   * empty event sets to prevent memory leaks.
   * 
   * @param event - The event name to unsubscribe from
   * @param handler - The exact handler function to remove
   */
  off(event: string, handler: EventHandler): void {
    const handlers = this.events.get(event);
    if (!handlers) return;
    
    // Check if this is a once handler wrapper
    const onceWrapper = this.onceHandlers.get(handler);
    if (onceWrapper) {
      handlers.delete(onceWrapper);
      this.onceHandlers.delete(handler);
    } else {
      handlers.delete(handler);
    }
    
    if (handlers.size === 0) {
      this.events.delete(event);
    }
  }
  
  /**
   * Subscribe to an event with a one-time handler.
   * 
   * Creates a wrapper function that calls the handler once then automatically
   * unsubscribes. Maps the original handler to the wrapper for manual cleanup.
   * 
   * @param event - The event name to subscribe to
   * @param handler - The function to call once when the event is emitted
   * @returns Unsubscribe function to remove this handler before it fires
   */
  once(event: string, handler: EventHandler): EventUnsubscribe {
    const onceWrapper: EventHandler = (...args) => {
      handler(...args);
      this.off(event, onceWrapper);
    };
    
    // Store mapping for potential manual removal
    this.onceHandlers.set(handler, onceWrapper);
    
    return this.on(event, onceWrapper);
  }
  
  /**
   * Emit an event synchronously to all registered handlers.
   * 
   * Calls all handlers immediately in registration order. Creates a copy of
   * the handler set to prevent issues if handlers modify the set during emission.
   * Catches and logs errors to prevent one failing handler from affecting others.
   * Also emits a wildcard '*' event for debugging and global monitoring.
   * 
   * @param event - The event name to emit
   * @param args - Arguments to pass to all event handlers
   */
  emit(event: string, ...args: any[]): void {
    const handlers = this.events.get(event);
    if (!handlers || handlers.size === 0) return;
    
    // Create a copy to avoid issues if handlers modify the set
    const handlersCopy = Array.from(handlers);
    
    for (const handler of handlersCopy) {
      try {
        handler(...args);
      } catch (error) {
        console.error(`Error in event handler for "${event}":`, error);
      }
    }
    
    // Also emit wildcard event for debugging/logging
    this.emit('*', event, ...args);
  }
  
  /**
   * Emit an event asynchronously to all registered handlers.
   * 
   * Calls all handlers concurrently using Promise.all and waits for all to complete.
   * Catches and logs errors from individual handlers to prevent failures from
   * affecting other handlers. Also emits a wildcard '*' event for monitoring.
   * 
   * @param event - The event name to emit
   * @param args - Arguments to pass to all event handlers
   * @returns Promise that resolves when all handlers complete
   */
  async emitAsync(event: string, ...args: any[]): Promise<void> {
    const handlers = this.events.get(event);
    if (!handlers || handlers.size === 0) return;
    
    const handlersCopy = Array.from(handlers);
    
    await Promise.all(
      handlersCopy.map(async (handler) => {
        try {
          await handler(...args);
        } catch (error) {
          console.error(`Error in async event handler for "${event}":`, error);
        }
      })
    );
    
    // Also emit wildcard event
    await this.emitAsync('*', event, ...args);
  }
  
  /**
   * Remove all listeners for a specific event or all events.
   * 
   * If an event name is provided, removes all handlers for that specific event.
   * If no event name is provided, clears all handlers for all events.
   * 
   * @param event - Optional event name to clear (clears all if omitted)
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
  }
  
  /**
   * Get the number of active listeners for an event.
   * 
   * Returns the count of currently registered handlers for the specified event.
   * Returns 0 if the event has no listeners or doesn't exist.
   * 
   * @param event - The event name to count listeners for
   * @returns Number of active listeners for the event
   */
  getListenerCount(event: string): number {
    return this.events.get(event)?.size || 0;
  }
  
  /**
   * Debug helper to get all active event names.
   * 
   * Returns an array of all event names that currently have registered handlers.
   * Useful for debugging, monitoring, and development tools.
   * 
   * @returns Array of active event names
   */
  getAllEvents(): string[] {
    return Array.from(this.events.keys());
  }
}

/**
 * Global event bus singleton for application-wide event coordination.
 * 
 * This singleton instance provides a shared event bus that can be used across
 * the entire application for cross-component communication. Use sparingly and
 * prefer dependency injection for better testability.
 * 
 * @example
 * ```typescript
 * import { globalEventBus } from './IEventBus';
 * 
 * // Subscribe to global events
 * globalEventBus.on('app-shutdown', () => {
 *   cleanup();
 * });
 * 
 * // Emit global events
 * globalEventBus.emit('user-logged-out');
 * ```
 */
export const globalEventBus = new EventBus();

/**
 * Type-safe event bus wrapper for strongly-typed event handling.
 * 
 * This generic wrapper provides compile-time type safety for event names and
 * their associated data types. Define an interface mapping event names to their
 * data types and get full TypeScript intellisense and type checking.
 * 
 * @template TEvents - Interface mapping event names to their data types
 * 
 * @example
 * ```typescript
 * interface AppEvents {
 *   'user-login': { userId: string; timestamp: Date };
 *   'data-updated': { entityId: string; changes: Record<string, any> };
 *   'error-occurred': { message: string; code: number };
 * }
 * 
 * const typedBus = new TypedEventBus<AppEvents>();
 * 
 * // Type-safe subscription
 * typedBus.on('user-login', (data) => {
 *   // data is typed as { userId: string; timestamp: Date }
 *   console.log(`User ${data.userId} logged in at ${data.timestamp}`);
 * });
 * 
 * // Type-safe emission
 * typedBus.emit('user-login', {
 *   userId: '123',
 *   timestamp: new Date()
 * });
 * 
 * // Compilation error for wrong event name or data type
 * // typedBus.emit('invalid-event', data); // Error!
 * // typedBus.emit('user-login', { wrong: 'data' }); // Error!
 * ```
 */
export class TypedEventBus<TEvents extends Record<string, any>> {
  /** Internal event bus instance for delegation */
  private eventBus = new EventBus();
  
  /**
   * Subscribe to a typed event with type-safe handler.
   * 
   * @template K - Event name key from the TEvents interface
   * @param event - The event name (must be a key of TEvents)
   * @param handler - Type-safe handler function for this event's data
   * @returns Unsubscribe function
   */
  on<K extends keyof TEvents>(
    event: K,
    handler: (data: TEvents[K]) => void
  ): EventUnsubscribe {
    return this.eventBus.on(event as string, handler);
  }
  
  /**
   * Unsubscribe from a typed event.
   * 
   * @template K - Event name key from the TEvents interface
   * @param event - The event name to unsubscribe from
   * @param handler - The exact handler function to remove
   */
  off<K extends keyof TEvents>(
    event: K,
    handler: (data: TEvents[K]) => void
  ): void {
    this.eventBus.off(event as string, handler);
  }
  
  /**
   * Subscribe to a typed event with a one-time type-safe handler.
   * 
   * @template K - Event name key from the TEvents interface
   * @param event - The event name to listen for once
   * @param handler - Type-safe handler function for this event's data
   * @returns Unsubscribe function
   */
  once<K extends keyof TEvents>(
    event: K,
    handler: (data: TEvents[K]) => void
  ): EventUnsubscribe {
    return this.eventBus.once(event as string, handler);
  }
  
  /**
   * Emit a typed event with type-safe data.
   * 
   * @template K - Event name key from the TEvents interface
   * @param event - The event name to emit
   * @param data - Type-safe data matching the event's expected type
   */
  emit<K extends keyof TEvents>(event: K, data: TEvents[K]): void {
    this.eventBus.emit(event as string, data);
  }
  
  /**
   * Emit a typed event asynchronously with type-safe data.
   * 
   * @template K - Event name key from the TEvents interface
   * @param event - The event name to emit
   * @param data - Type-safe data matching the event's expected type
   * @returns Promise that resolves when all handlers complete
   */
  async emitAsync<K extends keyof TEvents>(
    event: K,
    data: TEvents[K]
  ): Promise<void> {
    return this.eventBus.emitAsync(event as string, data);
  }
  
  /**
   * Remove all listeners for a typed event or all events.
   * 
   * @param event - Optional typed event name to clear (clears all if omitted)
   */
  removeAllListeners(event?: keyof TEvents): void {
    this.eventBus.removeAllListeners(event as string);
  }
}